import type { EmbeddedViewRef, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Directive, Input, TemplateRef, ViewContainerRef, Renderer2 } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevelopmentService } from '@shared/services';
import type { FeatureFlags } from '@shared/services/feature-flag/feature-flags';
import { FeatureFlagService } from '@shared/services/feature-flag/feature-flag.service';
import type { PermissionQuery } from '@shared/services/feature-flag/permission-expr';
import type { Subscription } from 'rxjs';

@UntilDestroy()
@Directive({
  selector: '[appFeatureFlag]',
})
export class FeatureFlagDirective implements OnInit, OnChanges {
  static readonly highlightColor = 'GoldenRod';

  @Input() public appFeatureFlag: FeatureFlags | PermissionQuery | undefined;
  @Input() public appFeatureFlagHighlight = true;

  private embeddedViewRef: EmbeddedViewRef<unknown>;
  private permissionSubscription: Subscription | null = null;

  private globalHighlightEnabled = false;

  private get isVisible(): boolean {
    return this.embeddedViewRef !== undefined && this.embeddedViewRef.rootNodes.length > 0;
  }

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private featureFlagService: FeatureFlagService,
    private develop: DevelopmentService,
    private renderer: Renderer2,
  ) {
    this.updateView(false);
  }

  public ngOnInit() {
    this.resubscribeToPermissions();

    this.develop.highlightMode.pipe(untilDestroyed(this)).subscribe((globalShowHighlight) => {
      this.globalHighlightEnabled = globalShowHighlight;
      this.updateHighlight();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes['appFeatureFlag']) {
      this.resubscribeToPermissions();
    }

    if (changes['appFeatureFlagHighlight']) {
      this.updateHighlight();
    }
  }

  private resubscribeToPermissions() {
    this.permissionSubscription?.unsubscribe();

    if (this.appFeatureFlag === undefined) {
      this.updateView(true);
      return;
    }

    this.permissionSubscription = this.featureFlagService
      .getPermission(this.appFeatureFlag)
      .pipe(untilDestroyed(this))
      .subscribe((enabled) => {
        this.updateView(enabled);
      });
  }

  private updateView(showContent: boolean) {
    if (this.isVisible === showContent) {
      return;
    }

    this.viewContainer.clear();
    if (showContent) {
      this.embeddedViewRef = this.viewContainer.createEmbeddedView(this.templateRef);
      this.updateHighlight();
    } else if (!showContent) {
      this.embeddedViewRef = undefined;
    }
  }

  private updateHighlight() {
    if (
      !this.embeddedViewRef ||
      this.appFeatureFlag === undefined ||
      !this.appFeatureFlagHighlight ||
      this.develop.isProd
    ) {
      return;
    }
    for (const node of this.embeddedViewRef.rootNodes) {
      if (!(node instanceof HTMLElement)) {
        continue;
      }

      if (this.globalHighlightEnabled) {
        this.renderer.setStyle(node, 'border', `1px solid ${FeatureFlagDirective.highlightColor}`);
      } else {
        this.renderer.removeStyle(node, 'border');
      }
    }
  }
}
